import { apiClient } from './api.client';

// ── Types ─────────────────────────────────────────────────────────────────────

export type LicenseStatus = 'Active' | 'Suspended' | 'Revoked' | 'Expired';

export interface LicenseActivationDto {
  id: number;
  machineFingerprint: string;
  hostname: string;
  ipAddress: string;
  activatedAt: string;
  lastHeartbeat: string | null;
  deactivatedAt: string | null;
  isActive: boolean;
  deactivateReason: string | null;
}

/** Returned only by the audited reveal endpoint — `rawKey` here is the complete key. */
export interface LicenseKeyReveal {
  id: number;
  rawKey: string;
  keyPrefix: string;
  companyName: string | null;
  licenseType: string;
  status: string;
  issuedAt: string;
  expiresAt: string;
}

export interface LicenseDto {
  id: number;
  companyId: number;
  companyName: string | null;
  unitId: number | null;
  unitName: string | null;
  rawKey: string;
  keyPrefix: string;
  status: LicenseStatus;
  issuedAt: string;
  expiresAt: string;
  maxActivations: number;
  activeInstances: number;
  features: string | null;
  issuedTo: string | null;
  notes: string | null;
  isTrial: boolean;
  suspendedAt: string | null;
  suspendReason: string | null;
  subscriptionId: number | null;
  activations: LicenseActivationDto[];
}

export interface IssueLicenseRequest {
  companyId: number;
  unitId?: number | null;
  expiresAt: string;
  maxActivations: number;
  features: string | null;
  issuedTo: string | null;
  notes: string | null;
  contactEmail?: string | null;
}

export interface IssueTrialRequest {
  companyId: number;
  unitId?: number | null;
  issuedTo: string | null;
  trialDays?: number;
  contactEmail?: string | null;
}

export interface UpdateLicenseRequest {
  issuedTo?:       string | null;
  notes?:          string | null;
  maxActivations?: number | null;
  expiresAt?:      string | null;  // ISO string
}

export interface IssueResult {
  licenseId: number;
  rawKey: string;
  warning: string;
}

// ── Service ───────────────────────────────────────────────────────────────────

export const licenseService = {
  async list(companyId?: number): Promise<LicenseDto[]> {
    const url = companyId ? `/license/admin/list?companyId=${companyId}` : '/license/admin/list';
    const res = await apiClient.get<LicenseDto[]>(url);
    return res.data;
  },

  async getById(id: number): Promise<LicenseDto> {
    const res = await apiClient.get<LicenseDto>(`/license/admin/${id}`);
    return res.data;
  },

  /**
   * The full license key. List and detail responses carry only the prefix, so this is the one way
   * to see the whole value — and the server records who asked before returning it.
   */
  async revealKey(id: number): Promise<LicenseKeyReveal> {
    const res = await apiClient.get<LicenseKeyReveal>(`/license/admin/${id}/full-key`);
    return res.data;
  },

  async issue(req: IssueLicenseRequest): Promise<IssueResult> {
    const res = await apiClient.post<IssueResult>('/license/admin/issue', req);
    return res.data;
  },

  async issueTrial(req: IssueTrialRequest): Promise<IssueResult> {
    const res = await apiClient.post<IssueResult>('/license/admin/trial', req);
    return res.data;
  },

  async revoke(id: number, reason: string): Promise<void> {
    await apiClient.put(`/license/admin/${id}/revoke`, { reason });
  },

  async convertTrial(id: number, expiresAt: string, maxActivations: number, features?: string, notes?: string): Promise<void> {
    await apiClient.put(`/license/admin/${id}/convert`, { expiresAt, maxActivations, features, notes });
  },

  async suspend(id: number, reason: string): Promise<void> {
    await apiClient.put(`/license/admin/${id}/suspend`, { reason });
  },

  async reactivate(id: number): Promise<void> {
    await apiClient.put(`/license/admin/${id}/reactivate`, {});
  },

  async renewLicense(id: number, expiresAt: string): Promise<void> {
    // Reactivate + extend expiry in one step (reactivate first, then update)
    await apiClient.put(`/license/admin/${id}/reactivate`, {});
    await apiClient.put(`/license/admin/${id}`, { expiresAt });
  },

  async deleteLicense(id: number, hard = true): Promise<void> {
    await apiClient.delete(`/license/admin/${id}?hard=${hard}`);
  },

  async updateLicense(id: number, req: UpdateLicenseRequest): Promise<void> {
    await apiClient.put(`/license/admin/${id}`, req);
  },
};

// ── Client-side activation types ──────────────────────────────────────────────

export interface LicenseStatusDto {
  licenseId: number;
  companyId: number;
  companyName: string | null;
  keyPrefix: string;
  status: LicenseStatus;
  issuedAt: string;
  expiresAt: string;
  maxActivations: number;
  activeInstances: number;
  features: string | null;
  issuedTo: string | null;
  isExpired: boolean;
  daysUntilExpiry: number;
  isInGracePeriod: boolean;
  gracePeriodEndsAt: string;
  daysUntilGraceEnds: number;
}

export interface ActivateLicenseRequest {
  rawKey: string;
  machineFingerprint: string;
  hostname: string;
  ipAddress: string;
  companyId?: number;
  unitId?: number;
}

// ── Verify response (step-1 key check, no activation slot consumed) ──────────
export interface LicenseVerifyResponse {
  licenseId:       number;
  companyName:     string;
  plan:            string;
  issuedTo:        string | null;
  issuedAt:        string;
  expiresAt:       string;
  daysUntilExpiry: number;
  modules:         string[];
}

export interface LicenseVerifyResult {
  valid: boolean;
  data:  LicenseVerifyResponse | null;
  error: string | null;
}

export interface LicenseActivationResult {
  success: boolean;
  sessionToken: string | null;
  tokenExpiresAt: string | null;
  error: string | null;
}

// ── Server-authoritative check result ─────────────────────────────────────────

export interface LicenseCheckResult {
  valid: boolean;
  /** 'ACTIVE' | 'NO_ACTIVATION' | 'EXPIRED' | 'SUSPENDED' | 'REVOKED' */
  reason: string;
  message: string | null;
  companyName: string | null;
  expiresAt: string | null;
  daysUntilExpiry: number;
}

// Module-level cache — avoids redundant round trips when LicenseGate mounts
// on every navigation. TTL is 5 minutes; invalidated immediately after
// activate/deactivate so the next gate check gets a fresh result.
let _checkCache: { result: LicenseCheckResult; ts: number } | null = null;
const CHECK_CACHE_TTL_MS = 5 * 60 * 1000;

export function getCachedCheckResult(): LicenseCheckResult | null {
  if (_checkCache && Date.now() - _checkCache.ts < CHECK_CACHE_TTL_MS) {
    return _checkCache.result;
  }
  return null;
}

export function invalidateLicenseCheckCache(): void {
  _checkCache = null;
}

// ── Legacy localStorage keys (kept for the embedded Settings dashboard) ───────
export const LICENSE_TOKEN_KEY        = 'ee_license_token';
export const LICENSE_TOKEN_EXPIRY_KEY = 'ee_license_token_expiry';
export const LICENSE_COMPANY_KEY      = 'ee_license_company_id';
export const LICENSE_UNIT_KEY         = 'ee_license_unit_id';

export const clientLicenseService = {

  // ── Server-authoritative check (primary gate mechanism) ─────────────────

  /**
   * Asks the server whether this installation has a valid active license.
   * The backend determines its own hardware fingerprint — no client-side token,
   * fingerprint, or localStorage access is required.
   *
   * Returns:
   *   LicenseCheckResult — server responded (valid or invalid)
   *   null               — network/server unreachable; caller should give benefit of the doubt
   */
  async check(skipCache = false): Promise<LicenseCheckResult | null> {
    if (!skipCache) {
      const cached = getCachedCheckResult();
      if (cached) return cached;
    }
    try {
      const res = await apiClient.get<LicenseCheckResult>('/license/check');
      _checkCache = { result: res.data, ts: Date.now() };
      return res.data;
    } catch (e: any) {
      if (!e?.response) return null; // network error → caller decides
      _checkCache = null;
      return { valid: false, reason: 'ERROR', message: 'License server error.', companyName: null, expiresAt: null, daysUntilExpiry: 0 };
    }
  },

  // ── Activation ────────────────────────────────────────────────────────────

  async verifyKey(rawKey: string): Promise<LicenseVerifyResult> {
    try {
      const res = await apiClient.post<LicenseVerifyResponse>('/license/verify', { rawKey });
      return { valid: true, data: res.data, error: null };
    } catch (e: any) {
      return {
        valid: false,
        data: null,
        error: e?.response?.data?.error ?? e?.response?.data?.message ?? 'Key verification failed.',
      };
    }
  },

  /**
   * Activate this HRMS installation. The server determines its own machine fingerprint —
   * only the license key and hostname are needed from the client.
   */
  async activate(req: { rawKey: string; hostname: string; ipAddress: string; companyId?: number }): Promise<{ success: boolean; error: string | null }> {
    try {
      await apiClient.post('/license/activate', req);
      invalidateLicenseCheckCache(); // force fresh DB check after activation
      return { success: true, error: null };
    } catch (e: any) {
      return {
        success: false,
        error: e?.response?.data?.error ?? e?.response?.data?.message ?? 'Activation failed.',
      };
    }
  },

  /**
   * Deactivate this installation — frees the activation slot.
   * The server uses its own hardware fingerprint; no token required from the client.
   */
  async deactivate(): Promise<void> {
    try {
      await apiClient.post('/license/deactivate');
    } finally {
      invalidateLicenseCheckCache();
    }
  },

  async getStatus(): Promise<LicenseStatusDto | null> {
    try {
      const res = await apiClient.get<LicenseStatusDto>('/license/status');
      return res.data;
    } catch {
      return null;
    }
  },
};
